import {
  ConversationItem as ConversationItemGenerated,
  GetInteractionAggregatesReqGroupBy,
  type GroupByQuery,
  type GroupByQueryTopicsConversationsItem,
  type GroupByQueryTopicsItem,
  PaginatedExtendedGroupByQuery,
  CategoryModel,
} from '@/api/generated/model';
import {
  ConversationItem,
  Emotion,
  InteractionAggregates,
  InteractionAggregatesPaginated,
  Topic,
  TopicsConversations,
} from '@/models/interaction-aggregates/internal-models';
import { AggregateBy } from '@/domains/dashboard/components/GlobalfiltersRow';

export type MapAPIDeps = {
  enableLLMIssuesV2?: boolean;
  aggregateBy: AggregateBy;
  aggregatesGroupBy?: GetInteractionAggregatesReqGroupBy;
  customAggregateCategories?: CategoryModel[];
  groupBy?: GetInteractionAggregatesReqGroupBy;
};

export function mapAPIToPaginatedInteractionAggregates(
  data: PaginatedExtendedGroupByQuery,
  deps?: MapAPIDeps,
): InteractionAggregatesPaginated {
  return {
    aggregates: data.data.map((d) => mapAPIToInteractionAggregates(d, deps)),
    nInteractions: data.n_interactions,
    nUsers: data.n_users,
    nSessions: data.n_sessions,
    totalInteractions: data.total_interactions,
    totalUsers: data.total_users,
    totalSessions: data.total_sessions,
    total: data.total,
    latestInteractionTimestamp: data.latest_interaction_timestamp,
    limit: data.limit,
    offset: data.offset,
  };
}

function mapAPIToInteractionAggregates(data: GroupByQuery, deps?: MapAPIDeps): InteractionAggregates {
  const { aggregateBy = 'interaction', customAggregateCategories } = deps ?? {};

  const isCustomEntity = customAggregateCategories?.some(
    (entity) => (entity.value === data.group_name || entity.value === data.group_name_2) && entity.is_custom,
  );

  return {
    bucket: data.bucket,
    groupName: data.group_name,
    groupName2: data.group_name_2,
    groupName3: data.group_name_3,
    groupName4: data.group_name_4,
    isCustom: data.is_custom,
    latencySeconds: data.latency_seconds,
    latencySecondsPrev: data.latency_seconds_prev,
    // === data sources ===
    nDataSources: data.n_data_sources,
    nDataSourcesPrev: data.n_data_sources_prev,
    // === emotions ===
    emotions: data.emotions?.map(mapAPIToEmotion) ?? [],
    nEmotions: data.n_emotions,
    nEmotionsPrev: data.n_emotions_prev,
    // === keywords ===
    nKeywords: data.n_keywords,
    nKeywordsPrev: data.n_keywords_prev,
    // === retention ===
    retention: data.retention,
    retentionPrev: data.retention_prev,
    // === sentiment ===
    sentimentScore: aggregateBy === 'interaction' ? data.sentiment_score : data.conversation_sentiment_score,
    sentimentScorePrev:
      aggregateBy === 'interaction' ? data.sentiment_score_prev : data.conversation_sentiment_score_prev,
    dropOffSentiment: data.dropoff_sentiment,
    dropOffSentimentPrev: data.dropoff_sentiment_prev,
    // === tags ===
    nTagValues: data.n_tag_values,
    tags: data.tags,
    // === topics ===
    nTopics: data.n_topics,
    nTopicsConversations: data.n_topics_conversations,
    nTopicsConversationsPrev: data.n_topics_conversations_prev,
    nTopicsPrev: data.n_topics_prev,
    topics: data.topics?.map(mapAPIToTopic) ?? [],
    topicsConversations: data.topics_conversations?.map(mapAPIToTopicsConversations) ?? [],
    // === user actions ===
    nUserActions: data.n_user_actions,
    nUserActionsConversations: data.n_user_actions_conversations,
    nUserActionsConversationsPrev: data.n_user_actions_conversations_prev,
    nUserActionsPrev: data.n_user_actions_prev,
    // === user intents ===
    nUserIntents: data.n_intents,
    // nUserIntentsConversations: data.n_intents_conversations,
    // nUserIntentsConversationsPrev: data.n_intents_conversations_prev,
    nUserIntentsPrev: data.n_intents_prev,
    // === negative intents ===
    nNegativeIntent: data.n_negative_intent,
    nNegativeIntentV2: data.n_negative_intent_v2,
    nNegativeIntentPrev: data.n_negative_intent_prev,
    nNegativeIntentPrevV2: data.n_negative_intent_prev_v2,
    // === interactions ===
    nInteractions: data.n_interactions,
    processedNInteractions: data.processed_n_interactions,
    nInteractionsNegativeV2: data.n_interactions_negative_v2,
    nInteractionsNegativePrevV2: data.n_interactions_negative_prev_v2,
    nInteractionsNegativeExplicitV2: data.n_interactions_negative_explicit_v2,
    nInteractionsNegativeExplicitPrevV2: data.n_interactions_negative_explicit_prev_v2,
    nInteractionsNegativeImplicitV2: data.n_interactions_negative_implicit_v2,
    nInteractionsNegativeImplicitPrevV2: data.n_interactions_negative_implicit_prev_v2,
    nInteractionsPrev: data.n_interactions_prev,
    // === feedbacks ===
    nFeedbackV2: data.n_feedback_v2,
    nFeedbackPrevV2: data.n_feedback_prev_v2,
    nNegativeExplicitUserFeedbackV2: data.n_negative_explicit_user_feedback_v2,
    nNegativeFeedback: data.n_negative_feedback,
    nNegativeFeedbackPrev: data.n_negative_feedback_prev,
    nNegativeImplicitUserFeedbackV2: data.n_negative_implicit_user_feedback_v2,
    nNegativeImplicitUserFeedbackPrevV2: data.n_negative_implicit_user_feedback_prev_v2,
    nPositiveExplicitUserFeedbackV2: data.n_positive_explicit_user_feedback_v2,
    nPositiveImplicitUserFeedbackV2: data.n_positive_implicit_user_feedback_v2,
    explicitFeedbackRatio: data.explicit_feedback_ratio,
    explicitFeedbackRatioPrev: data.explicit_feedback_ratio_prev,
    nPasteOutput: data.n_paste_output,
    nPasteOutputPrev: data.n_paste_output_prev,
    // === session (aka conversation) ===
    cost: data.cost,
    costPrev: data.cost_prev,
    conversation: mapAPIToConversationItem(data.conversation ?? []),
    nSessions: data.n_sessions,
    nSessionsPrev: data.n_sessions_prev,
    sessionLength: data.session_length,
    sessionLengthPrev: data.session_length_prev,
    endSession: data.end_session,
    endSessionPrev: data.end_session_prev,
    sessionTime: data.session_time,
    sessionTimePrev: data.session_time_prev,
    startSession: data.start_session,
    startSessionPrev: data.start_session_prev,
    // === messages ===
    nNegativeMessages: data.n_negative_messages,
    nNegativeMessagesPrev: data.n_negative_messages_prev,
    nPositiveMessages: data.n_positive_messages,
    nPositiveMessagesPrev: data.n_positive_messages_prev,
    // === users ===
    users: data.users ?? [],
    nUsers: data.n_users,
    nUsersPrev: data.n_users_prev,
    nUsersNegativeV2: data.n_users_negative_v2,
    nUsersNegativeExplicitV2: data.n_users_negative_explicit_v2,
    nUsersNegativeExplicitPrevV2: data.n_users_negative_explicit_prev_v2,
    nUsersNegativeImplicitV2: data.n_users_negative_implicit_v2,
    nUsersNegativeImplicitPrevV2: data.n_users_negative_implicit_prev_v2,
    nUsersNegativePrevV2: data.n_users_negative_prev_v2,
    // misc
    errorRate:
      (aggregateBy === 'interaction'
        ? data.percentage_negative_feedback
        : data.conversation_percentage_negative_feedback) ?? 0,
    errorRatePrev:
      (aggregateBy === 'interaction'
        ? data.percentage_negative_feedback_prev
        : data.conversation_percentage_negative_feedback_prev) ?? 0,
    errorRateExplicit:
      (aggregateBy === 'interaction'
        ? data.percentage_negative_feedback_explicit
        : data.conversation_percentage_negative_feedback_explicit) ?? 0,
    errorRateExplicitPrev:
      (aggregateBy === 'interaction'
        ? data.percentage_negative_feedback_explicit_prev
        : data.conversation_percentage_negative_feedback_explicit_prev) ?? 0,
    bounceRate: data.bounce_rate ?? 0,
    bounceRatePrev: data.bounce_rate_prev ?? 0,
    nConversationsPositiveExplicit: data.n_conversations_positive_explicit ?? 0,
    nConversationsPositiveExplicitPrev: data.n_conversations_positive_explicit_prev ?? 0,
    nConversationsNegativeExplicit: data.n_conversations_negative_explicit ?? 0,
    nConversationsNegativeExplicitPrev: data.n_conversations_negative_explicit_prev ?? 0,
    sortPercentage: data.sort_percentage ?? 0,
    sortPercentagePrev: data.sort_percentage_prev ?? 0,
    penetration: data.penetration ?? 0,
    penetrationPrev: data.penetration_prev ?? 0,
    outOfScopeRate: aggregateBy === 'interaction' ? data.out_of_scope_rate : (data.conversation_out_of_scope_rate ?? 0),
    outOfScopeRatePrev:
      aggregateBy === 'interaction' ? data.out_of_scope_rate_prev : (data.conversation_out_of_scope_rate_prev ?? 0),
    poorPromptQualityRate:
      aggregateBy === 'interaction' ? data.poor_prompt_quality_rate : (data.conversation_poor_prompt_quality_rate ?? 0),
    poorPromptQualityRatePrev:
      aggregateBy === 'interaction'
        ? data.poor_prompt_quality_rate_prev
        : (data.conversation_poor_prompt_quality_rate_prev ?? 0),
    userRating: data.user_rating ?? 0,
    userRatingPrev: data.user_rating_prev ?? 0,
    description: data.description ?? '',
    quote: data.quote ?? '',
    isCustomAggregateCategory: isCustomEntity,
    conversationCustomColumns: data.conversation_custom_columns ?? {},
    conversationId: data.conversation_id,
    conversationErrorStatus: data.conversation_error_status,
    roiHoursSaved: data.roi_hours_saved ?? 0,
    roiHoursSavedPrev: data.roi_hours_saved_prev ?? 0,
    roiHoursSavedPerUser: data.roi_hours_saved_per_user ?? 0,
    roiHoursSavedPerUserPrev: data.roi_hours_saved_per_user_prev ?? 0,
    roiHoursSavedPerInteraction: data.roi_hours_saved_per_interaction ?? 0,
    roiHoursSavedPerInteractionPrev: data.roi_hours_saved_per_interaction_prev ?? 0,
    roiSuccessfulInteractions: data.roi_successful_interactions ?? 0,
    roiSuccessfulInteractionsPrev: data.roi_successful_interactions_prev ?? 0,
    roiSuccessRate: data.roi_success_rate ?? 0,
    roiSuccessRatePrev: data.roi_success_rate_prev ?? 0,
    roiValueSaved: data.roi_value_saved ?? 0,
    roiValueSavedPrev: data.roi_value_saved_prev ?? 0,
    roiValueSavedPerInteraction: data.roi_value_saved_per_interaction ?? 0,
    roiValueSavedPerInteractionPrev: data.roi_value_saved_per_interaction_prev ?? 0,
    lastUploadDate: data.last_upload_date,
    customVariables: data.custom_variables ?? {},
    customVariablesPrev: data.custom_variables_prev ?? {},
    customCategoryCreatedAt: isCustomEntity
      ? customAggregateCategories?.find(
          (entity) => (entity.value === data.group_name || entity.value === data.group_name_2) && entity.is_custom,
        )?.created_at
      : undefined,
    inputLength: data.input_length,
    inputLengthPrev: data.input_length_prev,
    outputLength: data.output_length,
    outputLengthPrev: data.output_length_prev,
    inputTokens: data.input_tokens,
    inputTokensPrev: data.input_tokens_prev,
    outputTokens: data.output_tokens,
    outputTokensPrev: data.output_tokens_prev,
    totalUsers: data.total_users,
    aifiScore: data.aifi_score ?? undefined,
    aifiScorePrev: data.aifi_score_prev ?? undefined,
    // === conversation labels ===
    conversationLabels: data.conversation_labels ?? [],
  };
}

function mapAPIToEmotion(data: string): Emotion {
  return data;
}

function mapAPIToTopic(data: GroupByQueryTopicsItem): Topic {
  return data;
}

function mapAPIToTopicsConversations(data: GroupByQueryTopicsConversationsItem): TopicsConversations {
  return data;
}

function mapAPIToConversationItem(data: ConversationItemGenerated[]): ConversationItem[] {
  return data.map((item) => ({
    id: item.id,
    input: item.input,
    output: item.output,
    sentiment: item.sentiment,
    timestamp: item.timestamp,
    end_timestamp: item.end_timestamp,
    topic: item.topic,
    userQuery: item.user_query,
  }));
}
